import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { CtaCard } from '../components/ui/call-to-action-cta';
import { FlowingMenu } from '../components/ui/flowing-menu';

gsap.registerPlugin(ScrollTrigger);

const ServicesPage = () => {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const processRef = useRef(null);
  const benefitsRef = useRef(null);
  const carouselRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const scrollByAmount = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 340;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Carousel images
  const carouselImages = [
    "https://static.hsglasercnc.com/img/gx/gx.webp",
    "https://static.hsglasercnc.com/img/products/gv.webp",
    "https://static.hsglasercnc.com/img/gxm/gxm.webp",
    "https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=1600&q=80",
    "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=1600&q=80"
  ];

  // Services data with 4 main categories and their subcategories
  const services = [
    {
      id: 1,
      title: "Sheet Laser Cutting",
      slug: "sheet-laser-cutting",
      serviceImage: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Flatlasersheet/flatsheet2.png",
      description: "Precision sheet laser cutting services using advanced PRIDELASER Fiber Laser technology. MS up to 25MM, SS up to 16MM, Copper up to 3MM. ±0.1mm accuracy for industrial applications.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Flatlasersheet/flatsheet2.png",
      color: "bg-primary",
      subcategories: [
        { name: "MS Sheets", description: "Mild Steel up to 25MM" },
        { name: "SS Sheets", description: "Stainless Steel up to 16MM" },
        { name: "Copper & Aluminum", description: "Copper up to 3MM" }
      ]
    },
    {
      id: 2,
      title: "Pipe Laser Cutting",
      slug: "pipe-laser-cutting",
      serviceImage: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Rolling/MVI_2544.00_00_33_05.Still012.png",
      description: "Advanced laser pipe and tube cutting for Round, Square, Rectangle, L Angle & C Channel profiles. 20-240mm diameter with ±0.1mm precision.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Rolling/MVI_2544.00_00_33_05.Still012.png",
      color: "bg-primary",
      subcategories: [
        { name: "Round Pipes", description: "Circular pipe cutting" },
        { name: "Square & Rectangle", description: "Profile tube cutting" },
        { name: "L Angle & C Channel", description: "Structural profile cutting" }
      ]
    },
    {
      id: 3,
      title: "CNC Bending",
      slug: "cnc-bending",
      serviceImage: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/CNC/MVI_2544.00_00_51_26.Still017.png",
      description: "Professional CNC sheet bending services with OPTIMUM 200 TON pressing force and 4300MM bending length. DELEM Da58 T controller for precise bending operations.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/CNC/MVI_2544.00_00_51_26.Still017.png",
      color: "bg-secondary",
      subcategories: [
        { name: "Sheet Bending", description: "Precision bending for flat metal sheets" },
        { name: "Box Bending", description: "Forming enclosures and box shapes" },
        { name: "Channel Bending", description: "U and V channel forming" }
      ]
    },
    {
      id: 4,
      title: "Rolling",
      slug: "rolling",
      serviceImage: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Rolling/image.png",
      description: "Sheet and plate rolling for cylindrical and conical shapes. Precision rolling for tanks, ducts, structural columns & custom curved components.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Rolling/image.png",
      color: "bg-secondary",
      subcategories: [
        { name: "Plate Rolling", description: "Large cylinder and cone forming" },
        { name: "Sheet Rolling", description: "Thin sheet curved components" },
        { name: "Cone Rolling", description: "Conical shape forming" }
      ]
    },
    {
      id: 5,
      title: "Laser Welding",
      slug: "laser-welding",
      serviceImage: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/LaserWelding/welding.jpeg",
      description: "State-of-the-art laser welding for strong, clean, and precise joints with minimal heat affected zone. Continuous & spot welding for SS, MS, and Aluminum.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/LaserWelding/welding.jpeg",
      color: "bg-primary",
      subcategories: [
        { name: "Continuous Welding", description: "Seamless laser weld joints" },
        { name: "Spot Welding", description: "Precision spot laser welds" },
        { name: "Micro Welding", description: "Delicate thin material welding" }
      ]
    },
    {
      id: 6,
      title: "TIG Welding",
      slug: "tig-welding",
      serviceImage: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=500&h=500&fit=crop&q=80",
      description: "Tungsten Inert Gas welding for superior quality and precision on critical applications. AOTAI Inverter with 250-400 AMPS for SS, Aluminum & more.",
      image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1600&q=80",
      color: "bg-primary",
      subcategories: [
        { name: "SS Welding", description: "Stainless steel precision welding" },
        { name: "Aluminum Welding", description: "AC/DC aluminum welding" },
        { name: "Thin Gauge", description: "Delicate material welding" }
      ]
    },
    {
      id: 7,
      title: "MIG Welding",
      slug: "mig-welding",
      serviceImage: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Tig&Mig/Mig-Welding.jpeg",
      description: "Metal Inert Gas welding for high-speed and efficient fabrication on production runs. AOTAI Inverter with 250-400 AMPS for industrial fabrication.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Tig&Mig/Mig-Welding.jpeg",
      color: "bg-primary",
      subcategories: [
        { name: "Structural Welding", description: "Heavy structural steel fabrication" },
        { name: "Production Welding", description: "High-volume production runs" },
        { name: "MS Welding", description: "Mild steel fabrication" }
      ]
    },
    {
      id: 8,
      title: "Buffing",
      slug: "buffing",
      serviceImage: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/buffing/buffing.png",
      description: "Mirror finish buffing and polishing services. Surface finishing for stainless steel, aluminum & other metals to achieve premium aesthetics.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/buffing/buffing.png",
      color: "bg-primary",
      subcategories: [
        { name: "Mirror Finish", description: "High-gloss mirror polishing" },
        { name: "Satin Finish", description: "Smooth satin surface finish" },
        { name: "Metal Polishing", description: "Professional metal restoration" }
      ]
    },
    {
      id: 9,
      title: "Riveting",
      slug: "riveting",
      serviceImage: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/riveting/riveting.png",
      description: "Professional stud and standoff riveting services. Secure fastening solutions for sheet metal assemblies and enclosures.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/riveting/riveting.png",
      color: "bg-primary",
      subcategories: [
        { name: "Stud Riveting", description: "Precision stud fastening for secure joints" },
        { name: "Standoff Installation", description: "PCB & panel mounting standoffs" },
        { name: "PEM Fasteners", description: "Press-fit fastener installation" }
      ]
    },
    {
      id: 10,
      title: "Drilling & Tapping",
      slug: "drilling-tapping",
      serviceImage: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Drilling/MVI_2544.00_00_24_08.Still007.png",
      description: "Precision drilling and tapping services for threaded holes. CNC drilling for accurate hole placement and consistent threading.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Drilling/MVI_2544.00_00_24_08.Still007.png",
      color: "bg-primary",
      subcategories: [
        { name: "CNC Drilling", description: "Precision hole drilling with CNC accuracy" },
        { name: "Thread Tapping", description: "Accurate internal thread cutting" },
        { name: "Counter Boring", description: "Recessed hole creation for fasteners" }
      ]
    }
  ];

  const processSteps = [
    { number: "01", title: "Consultation", description: "Discuss your project requirements and specifications" },
    { number: "02", title: "Design", description: "Create detailed designs and technical drawings" },
    { number: "03", title: "Prototyping", description: "Develop and test initial prototypes" },
    { number: "04", title: "Production", description: "Execute precision manufacturing at scale" },
    { number: "05", title: "Quality Check", description: "Rigorous testing and quality assurance" },
    { number: "06", title: "Delivery", description: "On-time delivery with full documentation" }
  ];

  const benefits = [
    { image: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=200&h=200&fit=crop&q=80", title: "BODOR Fiber Laser Technology", description: "Latest fiber laser cutting technology for precision results and superior quality" },
    { image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=200&h=200&fit=crop&q=80", title: "Expert Team", description: "ISO certified technicians with 10+ years of manufacturing experience" },
    { image: "https://images.unsplash.com/photo-1625153669622-f71640d4074d?w=200&h=200&fit=crop&q=80", title: "24-48h Turnaround", description: "Fast delivery across Coimbatore, Chennai, Bangalore & South India" },
    { image: "https://images.unsplash.com/photo-1581093458791-9f3c3900df4b?w=200&h=200&fit=crop&q=80", title: "Competitive Pricing", description: "Best value for premium laser cutting & fabrication services" },
    { image: "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=200&h=200&fit=crop&q=80", title: "ISO 9001:2015 Certified", description: "100% quality guaranteed with rigorous inspection process" },
    { image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=200&h=200&fit=crop&q=80", title: "Pan-India Delivery", description: "Serving all major industrial cities with reliable logistics" }
  ];

  useEffect(() => {
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Auto-rotate carousel (only on desktop)
    let carouselInterval;
    if (!isMobile) {
      carouselInterval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
      }, 4000);
    }

    // Enhanced Hero section parallax animations
    const heroTl = gsap.timeline({
      scrollTrigger: {
        trigger: heroRef.current,
        start: "top top",
        end: "bottom top",
        scrub: 1,
      }
    });

    // Background overlay parallax
    heroTl.to(heroRef.current, {
      opacity: 0.7,
    });

    // Parallax effect on carousel images with different speeds
    if (carouselRef.current) {
      const images = carouselRef.current.querySelectorAll('.carousel-image');
      images.forEach((img, index) => {
        gsap.to(img, {
          yPercent: 40,
          scale: 1.2,
          ease: "none",
          scrollTrigger: {
            trigger: heroRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 1,
          }
        });
      });

      images.forEach((img, index) => {
        gsap.set(img, {
          opacity: index === 0 ? 1 : 0,
          scale: index === 0 ? 1 : 1.1
        });
      });
    }

    // Parallax on hero text with multi-layer effect
    const heroText = heroRef.current.querySelector('.hero-text');
    gsap.to(heroText, {
      yPercent: 80,
      scale: 1.3,
      opacity: 0,
      ease: "none",
      scrollTrigger: {
        trigger: heroRef.current,
        start: "top top",
        end: "bottom top",
        scrub: 1.5,
      }
    });

    // Parallax on carousel indicators
    const indicators = heroRef.current.querySelector('.carousel-indicators');
    if (indicators) {
      gsap.to(indicators, {
        yPercent: -100,
        opacity: 0,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        }
      });
    }

    // Services cards enhanced parallax animation
    const serviceCards = servicesRef.current.querySelectorAll('.service-card');
    serviceCards.forEach((card, index) => {
      // Entry animation
      gsap.fromTo(card,
        {
          opacity: 0,
          y: 120,
          scale: 0.85,
          rotateY: -20,
          rotateX: 10
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          rotateY: 0,
          rotateX: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 90%",
            end: "top 40%",
            scrub: 1.5,
          }
        }
      );

      // Continuous parallax effect while scrolling
      gsap.to(card, {
        y: -30,
        ease: "none",
        scrollTrigger: {
          trigger: card,
          start: "top bottom",
          end: "bottom top",
          scrub: 2,
        }
      });

      // Hover animation for card image
      const img = card.querySelector('.service-img');
      if (img) {
        card.addEventListener('mouseenter', () => {
          gsap.to(img, { scale: 1.15, duration: 0.6, ease: "power2.out" });
        });
        card.addEventListener('mouseleave', () => {
          gsap.to(img, { scale: 1, duration: 0.6, ease: "power2.out" });
        });
      }
    });

    // Services section header parallax
    const servicesHeader = servicesRef.current.querySelector('.section-header');
    if (servicesHeader) {
      gsap.to(servicesHeader, {
        y: -50,
        opacity: 0.8,
        ease: "none",
        scrollTrigger: {
          trigger: servicesRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1,
        }
      });
    }

    // Process timeline enhanced parallax animation
    const processSteps = processRef.current.querySelectorAll('.process-step');
    const processLine = processRef.current.querySelector('.process-line');

    gsap.fromTo(processLine,
      { scaleX: 0 },
      {
        scaleX: 1,
        duration: 2,
        ease: "power2.inOut",
        scrollTrigger: {
          trigger: processRef.current,
          start: "top 60%",
          end: "bottom 40%",
          scrub: 1,
        }
      }
    );

    processSteps.forEach((step, index) => {
      // Entry animation
      gsap.fromTo(step,
        {
          opacity: 0,
          y: 80,
          scale: 0.7,
          rotateZ: index % 2 === 0 ? -5 : 5
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          rotateZ: 0,
          duration: 0.8,
          ease: "back.out(1.7)",
          scrollTrigger: {
            trigger: step,
            start: "top 85%",
            end: "top 45%",
            scrub: 1.5,
          }
        }
      );

      // Continuous parallax effect
      const parallaxSpeed = index % 2 === 0 ? -20 : -40;
      gsap.to(step, {
        y: parallaxSpeed,
        ease: "none",
        scrollTrigger: {
          trigger: processRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 2,
        }
      });
    });

    // Process section header parallax
    const processHeader = processRef.current.querySelector('.section-header');
    if (processHeader) {
      gsap.to(processHeader, {
        y: -60,
        scale: 1.05,
        ease: "none",
        scrollTrigger: {
          trigger: processRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1,
        }
      });
    }

    // Background decorative elements parallax
    const processBg = processRef.current.querySelectorAll('.bg-decorative');
    processBg.forEach((bg, index) => {
      gsap.to(bg, {
        y: index % 2 === 0 ? 50 : -50,
        x: index % 2 === 0 ? -20 : 20,
        rotate: index % 2 === 0 ? 10 : -10,
        ease: "none",
        scrollTrigger: {
          trigger: processRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.5,
        }
      });
    });

    // Benefits section enhanced parallax
    const benefitItems = benefitsRef.current.querySelectorAll('.benefit-item');
    benefitItems.forEach((item, index) => {
      const isEven = index % 2 === 0;
      const column = index % 3;

      // Entry animation
      gsap.fromTo(item,
        {
          opacity: 0,
          x: isEven ? -120 : 120,
          y: 50,
          scale: 0.8,
          rotateY: isEven ? -15 : 15
        },
        {
          opacity: 1,
          x: 0,
          y: 0,
          scale: 1,
          rotateY: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: item,
            start: "top 90%",
            end: "top 50%",
            scrub: 1.5,
          }
        }
      );

      // Continuous parallax with different speeds per column
      const parallaxAmount = column === 0 ? -30 : column === 1 ? -50 : -40;
      gsap.to(item, {
        y: parallaxAmount,
        ease: "none",
        scrollTrigger: {
          trigger: benefitsRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 2,
        }
      });
    });

    // Benefits section header parallax
    const benefitsHeader = benefitsRef.current.querySelector('.section-header');
    if (benefitsHeader) {
      gsap.to(benefitsHeader, {
        y: -70,
        scale: 1.08,
        ease: "none",
        scrollTrigger: {
          trigger: benefitsRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1,
        }
      });
    }

    return () => {
      if (carouselInterval) clearInterval(carouselInterval);
      window.removeEventListener('resize', checkMobile);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [isMobile, carouselImages.length]);

  // Animate carousel slide change
  useEffect(() => {
    if (carouselRef.current) {
      const images = carouselRef.current.querySelectorAll('.carousel-image');
      images.forEach((img, index) => {
        if (index === currentSlide) {
          gsap.to(img, {
            opacity: 1,
            scale: 1,
            duration: 1,
            ease: "power2.out"
          });
        } else {
          gsap.to(img, {
            opacity: 0,
            scale: 1.1,
            duration: 1,
            ease: "power2.out"
          });
        }
      });
    }
  }, [currentSlide]);

  const [activeTab, setActiveTab] = useState('services');

  return (
    <div className="bg-gray-50">
      {/* Hero Section - Modern Layout */}
      <section ref={heroRef} className="relative min-h-screen bg-white overflow-hidden">
        {/* Main Content Grid */}
        <div className="w-full pt-32 pb-0 lg:pb-0">
          <div className="grid grid-cols-1 lg:grid-cols-[4fr_8fr] gap-10 items-stretch">
            {/* Left - Text Content */}
            <div className="space-y-8 pl-6 lg:pl-[max(1.5rem,calc((100%-1400px)/2+1.5rem))]">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-5xl md:text-7xl lg:text-8xl font-black text-steel uppercase leading-[0.9] tracking-tight"
              >
                Alpha Laser—<br />
                <span className="text-primary">Precision Metal</span><br />
                Solutions
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-steel-lighter text-xl max-w-lg leading-relaxed"
              >
                Alpha Laser Technologies delivers cutting-edge laser cutting, CNC bending, and fabrication services across South India.
              </motion.p>

              {/* Tabs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="flex gap-1 border-b border-gray-200"
              >
                <button
                  onClick={() => setActiveTab('services')}
                  className={`px-6 py-3 font-semibold text-sm uppercase tracking-wider transition-all ${
                    activeTab === 'services'
                      ? 'text-steel border-b-2 border-primary'
                      : 'text-steel-lighter hover:text-steel'
                  }`}
                >
                  Services
                </button>
                <button
                  onClick={() => setActiveTab('industries')}
                  className={`px-6 py-3 font-semibold text-sm uppercase tracking-wider transition-all ${
                    activeTab === 'industries'
                      ? 'text-steel border-b-2 border-primary'
                      : 'text-steel-lighter hover:text-steel'
                  }`}
                >
                  Industries
                </button>
              </motion.div>

              {/* Tab Content */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <p className="text-steel-lighter text-sm mb-4">
                  {activeTab === 'services' 
                    ? 'Explore our precision manufacturing services'
                    : 'Industries we serve across India'
                  }
                </p>
                
                {/* Search/Inquiry Form */}
                <div className="flex flex-wrap gap-3 items-center">
                  <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 rounded-lg flex-1 min-w-[200px]">
                    <Search className="w-5 h-5 text-steel-lighter" />
                    <input
                      type="text"
                      placeholder={activeTab === 'services' ? 'What service are you looking for?' : 'Which industry?'}
                      className="bg-transparent border-none outline-none text-steel placeholder:text-steel-lighter flex-1 text-sm"
                    />
                  </div>
                  <Link
                    to="/contact"
                    className="px-8 py-3 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-all duration-300 uppercase text-sm tracking-wider"
                  >
                    Get Quote
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Right - Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative min-h-[300px] md:min-h-[400px]"
            >
              <div className="relative overflow-hidden h-full">
                <img
                  src="https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/frame_041_delay-0.04s.webp"
                  alt="Precision Metal Fabrication"
                  className="w-full h-full object-cover"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section ref={servicesRef} className="py-20 px-6 bg-gray-50">
        <div className="max-w-[1400px] mx-auto">
          {/* Header Section */}
          <div className="section-header flex flex-col lg:flex-row lg:justify-between lg:items-start gap-8 mb-16">
            <div>
              <p className="text-primary font-semibold uppercase tracking-wide text-sm mb-4">/ Services We Offer</p>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-steel leading-[0.95]">
                Certified<br />Excellence
              </h2>
            </div>
            <div className="lg:max-w-md lg:pt-8">
              <p className="text-steel-lighter text-lg mb-6">
                From precision laser cutting to complete fabrication solutions, we've got you covered. Choose reliability, choose Alpha Laser.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/services" className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all">
                  View All Services <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 text-secondary font-semibold hover:gap-3 transition-all">
                  Call For Booking <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>

          {/* Desktop Service Cards - Horizontal Layout */}
          <div className="hidden lg:block relative">
            {/* Left Arrow */}
            <button
              onClick={() => scrollByAmount('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 -ml-5 w-12 h-12 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-all duration-300 text-steel"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Right Arrow */}
            <button
              onClick={() => scrollByAmount('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 -mr-5 w-12 h-12 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-all duration-300 text-steel"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <div
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto pb-8 scrollbar-hide mx-8"
            >
              {services.map((service, index) => (
                <Link
                  key={service.id}
                  to={`/services/${service.slug}`}
                  className="service-card group flex-shrink-0 w-[320px] relative"
                >
                  <motion.div
                    whileHover={{ y: -10 }}
                    className="relative h-[480px] rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-500"
                  >
                    {/* Image */}
                    <img
                      src={service.serviceImage}
                      alt={service.title}
                      draggable={false}
                      className="service-img absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-105 grayscale group-hover:grayscale-0 pointer-events-none"
                    />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent transition-all duration-500"></div>
                    
                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-8 transform transition-transform duration-500 ease-out group-hover:translate-y-[-8px]">
                      <h3 className="text-white font-bold text-xl mb-5 leading-tight transition-colors duration-300">
                        {service.title}
                      </h3>
                      
                      {/* Arrow Button */}
                      <div 
                        className="w-14 h-14 rounded-full bg-primary flex items-center justify-center transition-all duration-500 ease-out group-hover:bg-secondary group-hover:scale-110"
                      >
                        <ArrowRight className="w-7 h-7 text-white transform -rotate-45 transition-transform duration-500 ease-out group-hover:rotate-0" />
                      </div>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
            
            {/* Progress Bar */}
            <div className="mt-8 flex justify-center">
              <div className="w-64 h-1 bg-gray-200 rounded-full overflow-hidden">
                <div className="w-1/4 h-full bg-steel rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Mobile Service Cards - Swipe Carousel */}
          <div className="lg:hidden w-full overflow-hidden">
            <div 
              className="flex gap-4 px-4 overflow-x-auto snap-x snap-mandatory pb-6 scrollbar-hide"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {services.map((service, index) => (
                <Link
                  key={service.id}
                  to={`/services/${service.slug}`}
                  className="group block flex-shrink-0 w-[85vw] snap-center"
                >
                  <div className="relative rounded-2xl overflow-hidden shadow-lg h-[420px]">
                    {/* Background Image */}
                    <img
                      src={service.serviceImage}
                      alt={service.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />

                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>

                    {/* Number Badge */}
                    <div className="absolute top-4 left-4">
                      <div className={`w-10 h-10 rounded-xl ${service.color} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                        {String(index + 1).padStart(2, '0')}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-xl font-bold text-white mb-2 leading-tight">
                        {service.title}
                      </h3>
                      <p className="text-white/80 text-sm leading-relaxed mb-3 line-clamp-2">
                        {service.description}
                      </p>
                      {/* Subcategories */}
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {service.subcategories.slice(0, 3).map((sub, subIndex) => (
                          <span
                            key={subIndex}
                            className="px-2.5 py-1 bg-white/15 backdrop-blur-sm text-white text-[11px] font-medium rounded-full border border-white/20"
                          >
                            {sub.name}
                          </span>
                        ))}
                      </div>
                      {/* Arrow Button */}
                      <div className="inline-flex items-center gap-2 text-white text-sm font-semibold">
                        <span>Explore Service</span>
                        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                          <ArrowRight className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Swipe Indicator Dots */}
            <div className="flex justify-center gap-2 mt-2">
              {services.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-primary w-6' : 'bg-gray-300'} transition-all duration-300`}
                ></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How Does It Work Section */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left - Text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-5xl md:text-6xl font-black text-steel uppercase leading-[0.95] mb-8">
                How Does It<br />Work?
              </h2>
            </motion.div>

            {/* Right - Description */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <p className="text-primary font-semibold uppercase tracking-wider text-sm mb-4">
                Are you searching for precision manufacturing?
              </p>
              <p className="text-steel-lighter leading-relaxed">
                Alpha Laser Technologies connects your ideas with precision manufacturing. We are here to help you! Just reach out, share your requirements, choose from our services, and get your projects done with excellence.
              </p>
            </motion.div>
          </div>

          {/* Video Section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-16"
          >
            <p className="flex items-center gap-2 text-steel-lighter text-sm font-medium mb-4">
              <span className="w-2 h-2 rounded-full bg-primary" />
              Watch Our Video
            </p>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/fVjXZTMIB_A?si=s0kDRJ88q86JO0fx&vq=hd1080&rel=0&modestbranding=1"
                  title="Alpha Laser Technologies - Manufacturing Process"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process Timeline Section */}
      <section ref={processRef} className="py-20 px-6 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="bg-decorative absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
          <div className="bg-decorative absolute bottom-0 right-0 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="section-header text-center mb-16">
            <p className="text-secondary font-semibold uppercase tracking-wide text-sm mb-4">Our Process</p>
            <h2 className="text-4xl md:text-5xl font-bold text-steel mb-6">From Concept to Completion</h2>
            <p className="text-xl text-steel-lighter max-w-2xl mx-auto">
              A streamlined workflow ensuring precision and quality at every step
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                className="process-step group relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                {/* Connecting Line (desktop only) */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-primary/20 to-transparent z-0"></div>
                )}

                {/* Card */}
                <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 group-hover:border-primary/30 overflow-hidden">
                  {/* Background Pattern */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>

                  {/* Number Badge */}
                  <div className="relative mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl text-white font-bold text-2xl shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      {step.number}
                    </div>
                    {/* Decorative dot */}
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full animate-pulse"></div>
                  </div>

                  {/* Content */}
                  <div className="relative">
                    <h3 className="text-2xl font-bold text-steel mb-3 group-hover:text-primary transition-colors duration-300">
                      {step.title}
                    </h3>
                    <p className="text-steel-lighter leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Hover Arrow */}
                  <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                    <ArrowRight className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Timeline Progress Indicator */}
          <div className="mt-16 flex items-center justify-center gap-2">
            {processSteps.map((_, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 + 0.5 }}
                className={`h-2 rounded-full bg-primary ${index === 0 ? 'w-12' : index === processSteps.length - 1 ? 'w-12' : 'w-8'
                  }`}
              ></motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section - FlowingMenu Design */}
      <section ref={benefitsRef} className="py-32 px-6 relative overflow-hidden">
        {/* Wave Background with Gradient */}
        <div className="absolute inset-0 z-0">
          {/* Background Image */}
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=1600&q=80)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundAttachment: 'fixed'
            }}
          ></div>
          {/* Gradient overlay on top of image */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/70 via-gray-100/60 to-slate-200/70"></div>
          
          {/* Top wave SVG */}
          <svg className="absolute top-0 left-0 w-full" viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ height: '120px' }}>
            <path fill="rgba(255,255,255,0.3)" d="M0,64 C480,150 960,-20 1440,64 L1440,0 L0,0 Z"></path>
            <path fill="rgba(255,255,255,0.2)" d="M0,32 C360,100 1080,0 1440,80 L1440,0 L0,0 Z"></path>
            <path fill="#f9fafb" d="M0,0 C320,80 720,20 1440,60 L1440,0 L0,0 Z"></path>
          </svg>
          
          {/* Bottom wave SVG */}
          <svg className="absolute bottom-0 left-0 w-full rotate-180" viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ height: '120px' }}>
            <path fill="rgba(255,255,255,0.3)" d="M0,64 C480,150 960,-20 1440,64 L1440,0 L0,0 Z"></path>
            <path fill="rgba(255,255,255,0.2)" d="M0,32 C360,100 1080,0 1440,80 L1440,0 L0,0 Z"></path>
            <path fill="#f9fafb" d="M0,0 C320,80 720,20 1440,60 L1440,0 L0,0 Z"></path>
          </svg>
        </div>

        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
          <motion.div
            className="absolute top-20 left-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              x: [0, 50, 0],
              y: [0, 30, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.3, 1],
              x: [0, -50, 0],
              y: [0, -30, 0],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="section-header text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm font-medium text-primary uppercase tracking-[0.2em] mb-4">Why Choose Us</p>
              <h2 className="text-5xl md:text-6xl font-bold text-steel mb-6 tracking-tight">
                The Alpha <span className="text-primary">Advantage</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full mb-6"></div>
              <p className="text-xl text-steel-lighter max-w-2xl mx-auto">
                Discover what sets us apart in the manufacturing industry
              </p>
            </motion.div>
          </div>

          {/* FlowingMenu Component */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ height: '600px', position: 'relative' }}
            className="rounded-2xl overflow-hidden shadow-2xl bg-white border border-gray-200"
          >
            <FlowingMenu items={benefits} />
          </motion.div>

          {/* Simple decorative element at bottom */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="mt-20 text-center"
          >
            <div className="inline-flex items-center gap-3">
              <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent"></div>
              <span className="text-sm text-steel-lighter font-medium uppercase tracking-wider">Trusted by Industry Leaders</span>
              <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <div className="pt-32 pb-40 px-8 bg-gray-50 max-w-7xl mx-auto">
        <CtaCard
          imageSrc="https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=1600&q=80"
          title="Ready to Start Your Project?"
          description="Let's transform your ideas into precision reality with our expert manufacturing services. Get a free quote today!"
          inputPlaceholder="Enter your email"
          buttonText="Get Started"
          onButtonClick={(email) => {
            console.log('Email submitted:', email);
            // Handle email submission here
          }}
        />
      </div>
    </div>
  );
};

export default ServicesPage;
